import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Printer, Trash2, Wallet } from 'lucide-react';
import { printHtmlSilently } from '../lib/silentPrint';
import { loadCompanySettings } from '../hooks/useCompanySettings';
import { valorPorExtenso } from '../lib/extenso';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const todayStr = () => new Date().toISOString().substring(0, 10);
const money = (v) => Number(v || 0).toFixed(2).replace('.', ',');
const escapeHtml = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export const printAdvanceReceipt = async (emp, adv) => {
  const cfg = await loadCompanySettings().catch(() => null);
  const store = cfg?.trade_name || cfg?.legal_name || 'Smart Flow';
  const dateStr = new Date(adv.date).toLocaleDateString('pt-BR');
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Comprovante de Vale</title>
<style>
  @page { size: 80mm auto; margin: 3mm; }
  body { font-family: 'Courier New', monospace; font-size: 11px; margin: 0; padding: 0; color: #000; }
  .center { text-align: center; } .bold { font-weight: 700; }
  .row { display: flex; justify-content: space-between; }
  hr { border: none; border-top: 1px dashed #000; margin: 4px 0; }
  .big { font-size: 13px; font-weight: 700; }
  .lbl { font-size: 10px; text-transform: uppercase; }
</style></head><body>
  <div class="center bold">${escapeHtml(store)}</div>
  ${cfg?.cnpj ? `<div class="center">CNPJ: ${escapeHtml(cfg.cnpj)}</div>` : ''}
  <div class="center bold">==============================</div>
  <div class="center bold">COMPROVANTE DE VALE</div>
  <div class="center"><span class="lbl">Adiantamento salarial</span></div>
  <div class="center bold">==============================</div>
  <div class="row"><span class="lbl">Data:</span><span>${escapeHtml(dateStr)}</span></div>
  <div><span class="lbl">Funcionario:</span></div>
  <div class="bold">${escapeHtml(emp.name)}</div>
  ${emp.role ? `<div><span class="lbl">Funcao:</span> ${escapeHtml(emp.role)}</div>` : ''}
  ${emp.cpf ? `<div><span class="lbl">CPF:</span> ${escapeHtml(emp.cpf)}</div>` : ''}
  <hr/>
  <div class="row big"><span>VALOR:</span><span>R$ ${money(adv.amount)}</span></div>
  <div><span class="lbl">Por extenso:</span></div>
  <div class="bold">(${escapeHtml(valorPorExtenso(adv.amount))})</div>
  <hr/>
  ${adv.notes ? `<div><span class="lbl">Obs:</span> ${escapeHtml(adv.notes)}</div><hr/>` : ''}
  <div>Declaro ter recebido o valor acima a titulo de adiantamento salarial, a ser descontado na folha de pagamento do mes ${escapeHtml(adv.reference_month || '')}.</div>
  <br/><br/>
  <div class="center">______________________________</div>
  <div class="center">${escapeHtml(emp.name)}</div>
  <div class="center lbl">Assinatura do funcionario</div>
  <br/><br/>
  <div class="center">______________________________</div>
  <div class="center lbl">Responsavel: ${escapeHtml(adv.created_by_name || '')}</div>
  <hr/>
  <div class="center" style="font-size:10px;">ID ${escapeHtml(adv.id || '')}</div>
  <div class="center">Smart Flow POS</div>
  <br/><br/>
</body></html>`;
  await printHtmlSilently(html);
};

export const EmployeeAdvances = ({ employee, onChanged }) => {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ date: todayStr(), amount: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  const load = async () => {
    const r = await axios.get(`${API}/employees/advances`, { params: { employee_id: employee.id }, withCredentials: true });
    setList(r.data);
  };
  useEffect(() => { load(); }, [employee.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const pendente = list.filter((a) => a.status === 'pendente').reduce((s, a) => s + a.amount, 0);

  const submit = async (print) => {
    const amount = parseFloat(form.amount);
    if (!(amount > 0)) { setMsg({ type: 'err', text: 'Informe o valor do vale' }); return; }
    setBusy(true); setMsg({ type: '', text: '' });
    try {
      const r = await axios.post(`${API}/employees/advances`, {
        employee_id: employee.id, date: new Date(`${form.date}T12:00:00`).toISOString(), amount, notes: form.notes || null,
      }, { withCredentials: true });
      setForm({ date: todayStr(), amount: '', notes: '' });
      setMsg({ type: 'ok', text: `Vale de R$ ${money(amount)} registrado. Será descontado no próximo pagamento de salário.` });
      await load(); onChanged?.();
      if (print) await printAdvanceReceipt(employee, r.data);
    } catch (err) {
      setMsg({ type: 'err', text: err.response?.data?.detail || 'Erro ao registrar vale' });
    } finally { setBusy(false); }
  };

  const remove = async (adv) => {
    if (!window.confirm(`Excluir vale de R$ ${money(adv.amount)}? O valor será estornado ao caixa.`)) return;
    try {
      await axios.delete(`${API}/employees/advances/${adv.id}`, { withCredentials: true });
      await load(); onChanged?.();
    } catch (err) { setMsg({ type: 'err', text: err.response?.data?.detail || 'Erro ao excluir' }); }
  };

  return (
    <div className="space-y-4" data-testid="employee-advances">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-bold flex items-center gap-2"><Wallet size={18} className="text-blue-600" /> Vales de {employee.name}</h3>
        <span className="text-sm px-3 py-1 rounded-full bg-amber-100 text-amber-800 font-bold" data-testid="advances-pending-total">
          Pendente p/ desconto: R$ {money(pendente)}
        </span>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
        <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Registrar vale</p>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div className="space-y-1"><Label>Data *</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="advance-date" /></div>
          <div className="space-y-1"><Label>Valor (R$) *</Label><Input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0,00" data-testid="advance-amount" /></div>
          <div className="space-y-1 md:col-span-2"><Label>Observação</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="opcional" data-testid="advance-notes" /></div>
          <Button type="button" disabled={busy} onClick={() => submit(false)} className="bg-blue-600 hover:bg-blue-700" data-testid="advance-save-btn">Registrar</Button>
          <Button type="button" disabled={busy} onClick={() => submit(true)} className="bg-slate-800 hover:bg-slate-900" data-testid="advance-save-print-btn"><Printer size={16} className="mr-2" /> Registrar e imprimir</Button>
        </div>
        {form.amount > 0 && <p className="text-xs text-slate-500 mt-2 italic" data-testid="advance-extenso">({valorPorExtenso(form.amount)})</p>}
        {msg.text && <p className={`text-sm mt-2 font-medium ${msg.type === 'ok' ? 'text-emerald-700' : 'text-red-600'}`} data-testid="advance-msg">{msg.text}</p>}
      </div>

      <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
        <table className="w-full" data-testid="advances-table">
          <thead className="bg-slate-50"><tr>
            <th className="px-4 py-2 text-left text-xs uppercase tracking-wider text-slate-500 font-bold">Data</th>
            <th className="px-4 py-2 text-right text-xs uppercase tracking-wider text-slate-500 font-bold">Valor</th>
            <th className="px-4 py-2 text-left text-xs uppercase tracking-wider text-slate-500 font-bold">Status</th>
            <th className="px-4 py-2 text-left text-xs uppercase tracking-wider text-slate-500 font-bold">Observação</th>
            <th className="px-4 py-2 text-right text-xs uppercase tracking-wider text-slate-500 font-bold">Ações</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-200">
            {list.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-slate-400">Nenhum vale registrado</td></tr>}
            {list.map((a) => (
              <tr key={a.id} className="hover:bg-slate-50" data-testid={`advance-row-${a.id}`}>
                <td className="px-4 py-2 text-sm">{new Date(a.date).toLocaleDateString('pt-BR')}</td>
                <td className="px-4 py-2 text-right font-bold text-red-600">R$ {money(a.amount)}</td>
                <td className="px-4 py-2">
                  {a.status === 'descontado'
                    ? <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700 font-bold" title={a.deducted_at ? `Descontado em ${new Date(a.deducted_at).toLocaleDateString('pt-BR')}` : ''}>Descontado</span>
                    : <span className="text-xs px-2 py-1 rounded bg-yellow-100 text-yellow-700 font-bold">Pendente</span>}
                </td>
                <td className="px-4 py-2 text-sm text-slate-600">{a.notes || '-'}</td>
                <td className="px-4 py-2 text-right">
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => printAdvanceReceipt(employee, a)} className="text-slate-700 hover:text-blue-600" title="Imprimir comprovante" data-testid={`advance-print-${a.id}`}><Printer size={16} /></button>
                    {a.status === 'pendente' && <button type="button" onClick={() => remove(a)} className="text-red-600" title="Excluir vale" data-testid={`advance-delete-${a.id}`}><Trash2 size={16} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
